import { Suspense, lazy, useEffect } from 'react';
import { Center, Loader } from '@mantine/core';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { getAccessToken, setAuthLostHandler } from './api/client';
import { AppShellLayout } from './layouts/AppShellLayout';
import { LoginPage } from './pages/LoginPage';
import { ProfilesPage } from './pages/ProfilesPage';
import { ProfileDetailPage } from './pages/ProfileDetailPage';
import { SegmentsPage } from './pages/SegmentsPage';
import { SegmentEditorPage } from './pages/SegmentEditorPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { TemplateEditorPage } from './pages/TemplateEditorPage';
import { MessagesPage } from './pages/MessagesPage';
import { MessageDetailPage } from './pages/MessageDetailPage';
import { ComposePage } from './pages/ComposePage';
import { JourneysPage } from './pages/JourneysPage';
import { SuppressionsPage } from './pages/SuppressionsPage';
import { EventsPage } from './pages/EventsPage';
import { AuditPage } from './pages/AuditPage';
import { SettingsPage } from './pages/SettingsPage';
import { RequireSession } from './components/RequireSession';

// Code-split the two heaviest pages.
//
// The journey editor pulls in React Flow and the overview pulls in Recharts —
// together roughly half the bundle. Loading both on every page visit taxes every
// operator with the weight of features they may not open in a given session, and
// the dashboard's first paint is the thing they experience most often.
const OverviewPage = lazy(() =>
  import('./pages/OverviewPage').then((m) => ({ default: m.OverviewPage })));
const JourneyEditorPage = lazy(() =>
  import('./pages/JourneyEditorPage').then((m) => ({ default: m.JourneyEditorPage })));

function PageFallback() {
  return (
    <Center py="xl">
      <Loader size="sm" />
    </Center>
  );
}

export function App() {
  const navigate = useNavigate();

  // The client cannot import the router, so it calls back here when a refresh
  // fails. Without this a dead session leaves the user staring at empty tables
  // rather than a sign-in form.
  useEffect(() => {
    setAuthLostHandler(() => {
      if (window.location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    });
    return () => setAuthLostHandler(null);
  }, [navigate]);

  return (
    <Routes>
      <Route
        path="/login"
        element={getAccessToken() ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        element={
          <RequireSession>
            <AppShellLayout />
          </RequireSession>
        }
      >
        <Route
          path="/"
          element={
            <Suspense fallback={<PageFallback />}>
              <OverviewPage />
            </Suspense>
          }
        />
        <Route path="/profiles" element={<ProfilesPage />} />
        <Route path="/profiles/:id" element={<ProfileDetailPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/segments" element={<SegmentsPage />} />
        <Route path="/segments/:id" element={<SegmentEditorPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/templates/:id" element={<TemplateEditorPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/messages/:id" element={<MessageDetailPage />} />
        <Route path="/compose" element={<ComposePage />} />
        <Route path="/journeys" element={<JourneysPage />} />
        <Route
          path="/journeys/:id"
          element={
            <Suspense fallback={<PageFallback />}>
              <JourneyEditorPage />
            </Suspense>
          }
        />
        <Route path="/suppressions" element={<SuppressionsPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
